// Reward Point Management.
//
// Operators can:
//   - Browse customers + their current points / tier (search by name or email)
//   - Recalculate one customer's points from their last 12 months of completed jobs
//   - Recalculate ALL customers' points (the monthly batch action)
//
// Recalc rule (server-side, see admin.ts §Rewards): 1 HKD spent on a
// completed job = 1 reward point; tier follows reward_levels thresholds.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { tokens } from "@findgeeks/ui-tokens";
import { apiFetch, ApiError } from "@/lib/api";
import { DataTable, type DataTableColumn } from "@/components/DataTable";

interface RewardCustomer {
  user_id: string;
  level: "silver" | "gold" | "diamond";
  reward_points: number;
  user: {
    id: string;
    display_name: string | null;
    email: string | null;
  } | null;
}

interface RewardListResp {
  items: RewardCustomer[];
  total: number;
  page: number;
  limit: number;
}

interface RecalcAllResp {
  processed: number;
  upgraded: number;
  downgraded: number;
  failed: number;
  total_customers: number;
  truncated: boolean;
  max_per_call: number;
  cutoff_at: string;
  completed_at: string;
}

interface RecalcOneResp {
  ok: true;
  customer_id: string;
  points: number;
  level: string;
  total_cents: number;
  jobs_counted: number;
  cutoff_at: string;
}

const TIER_LABEL: Record<string, string> = {
  silver: "Silver",
  gold: "Gold",
  diamond: "Diamond",
};

const TIER_COLOR: Record<string, string> = {
  silver: "#9CA3AF",
  gold: "#C9A227",
  diamond: "#1E4CF8",
};

// Map raw API error codes to operator-readable messages. Falls back to the
// raw message for codes we haven't translated yet.
function friendlyError(e: unknown): string {
  if (e instanceof ApiError) {
    const code = (e.body as { error?: string } | null)?.error ?? e.message;
    switch (code) {
      case "customer_not_found":
        return "Customer not found.";
      case "recalc_failed":
        return "Recalculation failed — check the worker logs for details.";
      case "db_error":
        return "Database error — please try again.";
      default:
        return `${e.message} (status ${e.status})`;
    }
  }
  return String(e);
}

export function RewardManagement() {
  const qc = useQueryClient();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const pageSize = 20;

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [snack, setSnack] = useState<{ msg: string; severity: "success" | "error" | "warning" } | null>(null);

  const list = useQuery<RewardListResp>({
    queryKey: ["admin", "rewards", "customers", page, pageSize, search],
    queryFn: () =>
      apiFetch<RewardListResp>(
        `/v1/admin/rewards/customers?page=${page + 1}&limit=${pageSize}&q=${encodeURIComponent(search.trim())}`,
      ),
  });

  const recalcAll = useMutation<RecalcAllResp>({
    mutationFn: () =>
      apiFetch<RecalcAllResp>("/v1/admin/rewards/recalculate", {
        method: "POST",
      }),
    onSuccess: (res) => {
      setConfirmOpen(false);
      const base = `Recalculated ${res.processed} customer${res.processed === 1 ? "" : "s"} (${res.upgraded} upgraded · ${res.downgraded} downgraded · ${res.failed} failed)`;
      const trunc = res.truncated
        ? ` — capped at ${res.max_per_call}; run again to process the rest`
        : "";
      setSnack({
        msg: base + trunc,
        severity: res.truncated ? "warning" : "success",
      });
      qc.invalidateQueries({ queryKey: ["admin", "rewards"] });
    },
    onError: (e) => {
      setConfirmOpen(false);
      setSnack({ msg: friendlyError(e), severity: "error" });
    },
  });

  const recalcOne = useMutation<RecalcOneResp, Error, string>({
    mutationFn: (customerId) =>
      apiFetch<RecalcOneResp>(
        `/v1/admin/rewards/recalculate/${customerId}`,
        { method: "POST" },
      ),
    onSuccess: (res) => {
      setSnack({
        msg: `Recalculated: ${res.points} pts · ${TIER_LABEL[res.level] ?? res.level} (${res.jobs_counted} jobs counted)`,
        severity: "success",
      });
      qc.invalidateQueries({ queryKey: ["admin", "rewards"] });
    },
    onError: (e) => {
      setSnack({ msg: friendlyError(e), severity: "error" });
    },
  });

  const cardSx = {
    border: `1px solid ${tokens.colors.admin.border}`,
    borderRadius: 1,
  };

  const columns: DataTableColumn<RewardCustomer>[] = [
    {
      field: "name",
      headerName: "Name",
      render: (r) => r.user?.display_name || "—",
    },
    {
      field: "email",
      headerName: "Email",
      render: (r) => r.user?.email || "—",
    },
    {
      field: "reward_points",
      headerName: "Points",
      render: (r) => r.reward_points.toLocaleString("en-HK"),
    },
    {
      field: "level",
      headerName: "Tier",
      render: (r) => (
        <Box
          component="span"
          sx={{
            display: "inline-block",
            px: 1.25,
            py: 0.25,
            borderRadius: 999,
            backgroundColor: `${TIER_COLOR[r.level] ?? "#9CA3AF"}22`,
            color: TIER_COLOR[r.level] ?? "#9CA3AF",
            fontWeight: 700,
            fontSize: 12,
          }}
        >
          {TIER_LABEL[r.level] ?? r.level}
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "",
      render: (r) => (
        <Button
          size="small"
          variant="outlined"
          disabled={
            recalcOne.isPending && recalcOne.variables === r.user_id
          }
          onClick={() => recalcOne.mutate(r.user_id)}
        >
          {recalcOne.isPending && recalcOne.variables === r.user_id
            ? "…"
            : "Recalculate"}
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header / batch action card */}
      <Paper elevation={0} sx={{ ...cardSx, p: 3, mb: 3 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 600,
                color: tokens.colors.admin.textHeading,
              }}
            >
              Reward Point Management
            </Typography>
            <Typography
              sx={{ fontSize: 13, color: tokens.colors.admin.textBody, mt: 0.5 }}
            >
              Recalculate every customer's reward balance from their last 12
              months of completed jobs. 1 HKD spent = 1 point. Tiers update
              automatically based on the new balance.
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            disabled={recalcAll.isPending}
            onClick={() => setConfirmOpen(true)}
            sx={{ minWidth: 220, py: 1.25 }}
          >
            {recalcAll.isPending ? "Recalculating…" : "Recalculate All Rewards"}
          </Button>
        </Stack>
      </Paper>

      {/* Customer list card */}
      <Paper elevation={0} sx={{ ...cardSx, p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 600,
              color: tokens.colors.admin.textHeading,
              flex: 1,
            }}
          >
            Customers
          </Typography>
          <TextField
            size="small"
            placeholder="Search name or email"
            value={search}
            onChange={(e) => {
              setPage(0);
              setSearch(e.target.value);
            }}
            sx={{ minWidth: 280 }}
          />
        </Stack>

        {list.isError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load customers.
          </Alert>
        ) : null}

        <DataTable
          rows={list.data?.items ?? []}
          columns={columns}
          loading={list.isLoading}
          getRowId={(r) => r.user_id}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(m) => setPage(m.page)}
          rowCount={list.data?.total ?? 0}
          emptyMessage="No customers found."
        />
      </Paper>

      {/* Confirm dialog for "Recalculate All" */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Recalculate all customers?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will overwrite every customer's reward balance and tier based
            on their completed jobs over the last 12 months. The action is
            idempotent (running it again gives the same result if no jobs
            change), but it cannot be selectively undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => recalcAll.mutate()}
            disabled={recalcAll.isPending}
          >
            {recalcAll.isPending ? "Working…" : "Recalculate"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Result snackbar */}
      <Snackbar
        open={snack !== null}
        autoHideDuration={6000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack?.severity ?? "success"}
          onClose={() => setSnack(null)}
          sx={{ width: "100%" }}
        >
          {snack?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
